import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { LuFilter } from "react-icons/lu";
import { FiChevronDown } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";

const PANEL_MAX_HEIGHT = 300;

export default function TableFilters({ columns = [], filterValues = {}, onChange, nowrap = false }) {
  const { t } = useTranslation("common");
  const [openField, setOpenField] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const anchorRef = useRef(null);

  // Keep the open dropdown attached to its button when the page moves, rather
  // than closing it. On phones the search box's autofocus opens the keyboard,
  // which resizes and scrolls the page, so closing on those events shut the
  // dropdown the moment it opened. A click outside, Escape or picking an
  // option still closes it.
  useEffect(() => {
    if (!openField) return undefined;

    let frame = null;
    const reposition = (event) => {
      // Scrolling the option list itself doesn't move the button.
      if (
        event?.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      if (frame !== null) return;

      frame = requestAnimationFrame(() => {
        frame = null;
        if (anchorRef.current) {
          setAnchorRect(anchorRef.current.getBoundingClientRect());
        }
      });
    };

    const viewport = window.visualViewport;
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    viewport?.addEventListener("resize", reposition);
    viewport?.addEventListener("scroll", reposition);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      viewport?.removeEventListener("resize", reposition);
      viewport?.removeEventListener("scroll", reposition);
    };
  }, [openField]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${highlightedIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // A column can bring extra dropdowns through `extraFilters` (each with its
  // own field_name, label and filterOptions), shown right after its own.
  const filterable = columns
    .filter((c) => c?.checked !== false)
    .flatMap((c) => [c, ...(c?.extraFilters ?? [])])
    .filter((c) => Array.isArray(c?.filterOptions) && c.filterOptions.length);

  if (!filterable.length) return null;

  const selectedLabel = (col) => {
    const val = filterValues[col.field_name];
    if (val === undefined || val === null || val === "") return null;
    return col.filterOptions.find((o) => String(o.value) === String(val))?.label ?? val;
  };

  const handleSelect = (col, value) => {
    onChange?.(col.field_name, value);
    setOpenField(null);
  };

  return (
    <div
      className={`flex items-center gap-[7px] ${nowrap ? "flex-nowrap" : "flex-wrap"}`}
    >
      {filterable.map((col) => {
        const active = selectedLabel(col);
        const Icon = col.filterIcon || LuFilter;
        const isOpen = openField === col.field_name;

        return (
          <div
            key={col.field_name}
            className={`relative ${nowrap ? "shrink-0" : ""}`}
          >
            <button
              type="button"
              onClick={(event) => {
                anchorRef.current = event.currentTarget;
                setAnchorRect(
                  event.currentTarget.getBoundingClientRect(),
                );
                setOpenField(isOpen ? null : col.field_name);
                setSearchTerm("");
                setHighlightedIndex(0);
              }}
              title={active ? `${col.label}: ${active}` : col.label}
              className={`flex max-w-[190px] items-center gap-[5px] rounded-md border px-[11px] py-[5px] text-xs font-medium whitespace-nowrap transition-colors ${
                active
                  ? "border-state-info-border bg-brand-light text-brand"
                  : "border-ui-border bg-ui-surface text-ui-text-muted hover:border-brand hover:text-brand"
              }`}
            >
              <Icon className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {col.label}
                {active ? `: ${active}` : ""}
              </span>
              {active ? (
                <IoClose
                  className="h-3 w-3 shrink-0 hover:text-state-danger-text"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(col, "");
                  }}
                />
              ) : (
                <FiChevronDown className="h-[10px] w-[10px] shrink-0 opacity-60" />
              )}
            </button>

            {isOpen && (() => {
              const filteredOptions = col.filterOptions.filter(
                (opt) =>
                  !searchTerm ||
                  opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
              );

              const selectHighlighted = () => {
                if (highlightedIndex === 0) {
                  handleSelect(col, "");
                } else if (filteredOptions[highlightedIndex - 1]) {
                  handleSelect(col, filteredOptions[highlightedIndex - 1].value);
                }
              };

              // Fit the panel into the part of the screen that is actually
              // visible, which is above the keyboard when one is open.
              const viewport = window.visualViewport;
              const visibleTop = viewport?.offsetTop ?? 0;
              const visibleBottom = viewport
                ? viewport.offsetTop + viewport.height
                : window.innerHeight;
              const top = Math.max(
                visibleTop + 8,
                Math.min(
                  (anchorRect?.bottom ?? 0) + 4,
                  visibleBottom - PANEL_MAX_HEIGHT - 8,
                ),
              );

              return createPortal(
                <>
                  <div
                    className="fixed inset-0 z-[58]"
                    onClick={() => setOpenField(null)}
                  />
                  <div
                    ref={panelRef}
                    style={{
                      top,
                      left: Math.max(
                        8,
                        Math.min(anchorRect?.left ?? 0, window.innerWidth - 206),
                      ),
                      maxHeight: Math.max(
                        80,
                        Math.min(PANEL_MAX_HEIGHT, visibleBottom - top - 8),
                      ),
                    }}
                    className="fixed z-[59] flex min-w-[190px] flex-col overflow-hidden rounded-lg border border-ui-border bg-ui-surface-raised shadow-ui-md"
                  >
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
                      {col.label}
                    </div>
                    <div className="px-2 pb-1.5">
                      <input
                        type="text"
                        autoFocus
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setHighlightedIndex(0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightedIndex((i) =>
                              Math.min(i + 1, filteredOptions.length),
                            );
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightedIndex((i) => Math.max(i - 1, 0));
                          } else if (e.key === "Enter") {
                            e.preventDefault();
                            selectHighlighted();
                          } else if (e.key === "Escape") {
                            setOpenField(null);
                          }
                        }}
                        placeholder={t("table.searchDots")}
                        className="w-full rounded-md border border-ui-border px-2 py-1 text-xs text-ui-text outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand/[0.08]"
                      />
                    </div>
                    <div
                      ref={listRef}
                      className="thin__scrollbar min-h-0 flex-1 overflow-y-auto py-1"
                    >
                      <button
                        type="button"
                        data-index={0}
                        onClick={() => handleSelect(col, "")}
                        className={`flex w-full items-center justify-between px-3 py-2 text-xs transition-colors hover:bg-ui-row-hover ${
                          highlightedIndex === 0 ? "bg-ui-row-hover" : ""
                        } ${!active ? "font-medium text-brand" : "text-ui-text-muted"}`}
                      >
                        {t("table.all")}
                        {!active && <MdCheck className="h-3.5 w-3.5" />}
                      </button>
                      {filteredOptions.map((opt, idx) => {
                        const isSel =
                          String(filterValues[col.field_name] ?? "") === String(opt.value);
                        return (
                          <button
                            key={String(opt.value)}
                            type="button"
                            data-index={idx + 1}
                            onClick={() => handleSelect(col, opt.value)}
                            className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-ui-row-hover ${
                              highlightedIndex === idx + 1 ? "bg-ui-row-hover" : ""
                            } ${isSel ? "bg-brand-light font-medium text-brand" : "text-ui-text"}`}
                          >
                            <span className="truncate">{opt.label}</span>
                            {isSel && <MdCheck className="h-3.5 w-3.5 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>,
                document.body,
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}
